// Ayuda viva "Facturación electrónica PERÚ (SUNAT)" — /financiero/ayuda-sunat.
// Exclusiva de empresas con pais='PE' (la asignación de menú es por empresa, script 688).
// Patrón ayuda-de-modulo: checklist EN VIVO (fe_pe_ayuda_estado) + conceptos +
// pasos de implementación + FAQ + glosario, todo con deep-links.
import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BadgeCheck, BookOpen, CheckCircle2, ChevronDown, ChevronRight, HelpCircle,
  FileX2, LifeBuoy, ListChecks, Loader2, Rocket, XCircle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { apiClient } from '@/lib/api/client';
import { useTabsStore } from '@/stores/tabs.store';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };

interface Estado {
  aplica: boolean; pais: string | null; empresa: string; ruc: string | null;
  checklist: {
    ruc: boolean; ambiente: string | null; series: boolean;
    serie_factura: string | null; serie_boleta: string | null; serie_nc: string | null;
    sol_usuario: boolean; sol_password: boolean; certificado: boolean; direccion: boolean;
    clientes: boolean; centro_costo: boolean; tipo_cambio_hoy: boolean; worker: boolean;
  };
  stats: {
    autorizados: number; encolados: number; rechazados: number; errores: number;
    boletas_pendientes: number; resumenes: number; ultimo_autorizado: string | null;
  };
}

function useIr() {
  const openTab = useTabsStore((s) => s.openTab);
  return (path: string, title: string) => openTab({ id: path, title, path });
}

function Ir({ to, title, children }: { to: string; title: string; children?: ReactNode }) {
  const ir = useIr();
  return (
    <button className="text-primary hover:underline text-xs font-medium" onClick={() => ir(to, title)}>
      {children ?? title} →
    </button>
  );
}

// Checklist: key empata con el flag de la RPC; advisory = no bloquea la operación
const CHECKS: { key: keyof Estado['checklist']; label: string; ruta?: string; titulo?: string; ayuda: string; advisory?: boolean }[] = [
  { key: 'ruc', label: 'RUC de la empresa (11 dígitos)', ruta: '/seguridad/empresas', titulo: 'Empresas', ayuda: 'El RUC identifica al emisor ante SUNAT: va en el XML, en el nombre de archivo y en el usuario del envío.' },
  { key: 'series', label: 'Series de comprobantes (F###, B###, FC##)', ruta: '/financiero/emision-config', titulo: 'Configuración de Emisión', ayuda: 'Factura = serie F (F001), Boleta = serie B (B001), Nota de crédito con su propia serie. El correlativo lo lleva el sistema.' },
  { key: 'sol_usuario', label: 'Usuario SOL secundario', ruta: '/financiero/emision-config', titulo: 'Configuración de Emisión', ayuda: 'Usuario secundario de Clave SOL con el perfil "Envío de comprobantes". En el ambiente de pruebas se usa MODDATOS.' },
  { key: 'sol_password', label: 'Clave SOL', ruta: '/financiero/emision-config', titulo: 'Configuración de Emisión', ayuda: 'Se guarda cifrada y nunca vuelve a mostrarse. En pruebas: MODDATOS.' },
  { key: 'certificado', label: 'Certificado digital .p12 (+ su clave)', ruta: '/financiero/emision-config', titulo: 'Configuración de Emisión', ayuda: 'Firma el XML de cada comprobante. En el ambiente de pruebas sirve un certificado de ensayo; en producción debe ser uno emitido por una entidad autorizada.' },
  { key: 'direccion', label: 'Dirección fiscal (domicilio)', ruta: '/financiero/emision-config', titulo: 'Configuración de Emisión', ayuda: 'Sale en el comprobante impreso (RIDE).' },
  { key: 'clientes', label: 'Al menos un cliente registrado', ruta: '/financiero/partner', titulo: 'Partner', ayuda: 'Con RUC (11 dígitos) sale FACTURA; con DNI u otro documento sale BOLETA.' },
  { key: 'centro_costo', label: 'Centro de costo', ruta: '/financiero/partner', titulo: 'Partner', ayuda: 'Toda factura exige un centro de costo (analítica contable).' },
  { key: 'tipo_cambio_hoy', label: 'Tipo de cambio PEN de hoy', ayuda: 'Lo descarga solo el sistema cada día (SBS). Solo se usa para el reporte consolidado en USD; si falta, se puede facturar indicándolo manualmente.', advisory: true },
  { key: 'worker', label: 'Motor de emisión conectado', ruta: '/sistemas/procesos', titulo: 'Monitor de procesos', ayuda: 'El proceso que firma y envía a SUNAT. Si está apagado, los comprobantes quedan "encolados" hasta que vuelva.', advisory: true },
];

const PASOS: { n: number; titulo: string; detalle: string; ruta?: string; tituloTab?: string }[] = [
  { n: 1, titulo: 'Completa los datos del emisor', detalle: 'RUC de 11 dígitos, razón social y dirección fiscal de la empresa.', ruta: '/seguridad/empresas', tituloTab: 'Empresas' },
  { n: 2, titulo: 'Configura el ambiente de PRUEBAS (beta)', detalle: 'En Configuración de Emisión elige ambiente Pruebas, usuario SOL MODDATOS, clave MODDATOS y un certificado .p12 de ensayo. Define las series F001 / B001.', ruta: '/financiero/emision-config', tituloTab: 'Configuración de Emisión' },
  { n: 3, titulo: 'Emite comprobantes de prueba', detalle: 'Crea un cliente con RUC y factura normalmente: el sistema genera el XML UBL, lo firma, lo envía a SUNAT beta y guarda el CDR. Verifica que quede AUTORIZADO y revisa el PDF (RIDE) con su código QR.' },
  { n: 4, titulo: 'Prueba también boletas, notas de crédito y bajas', detalle: 'La boleta (cliente con DNI) se reporta por Resumen Diario automático; la NC referencia a su factura; la baja anula comprobantes ya aceptados.', ruta: '/financiero/comunicacion-baja', tituloTab: 'Comunicación de Baja' },
  { n: 5, titulo: 'Tramita lo real ante SUNAT (fuera del sistema)', detalle: 'Compra un certificado digital a una entidad autorizada, crea en SOL un usuario secundario con perfil "Envío de comprobantes" y comunica a SUNAT tu incorporación como emisor electrónico por sistemas del contribuyente (incluye el proceso de homologación/pruebas que SUNAT exija).' },
  { n: 6, titulo: 'Pasa a PRODUCCIÓN', detalle: 'Cambia el ambiente a Producción y reemplaza usuario SOL, clave y certificado por los reales. Desde ahí, cada comprobante emitido tiene valor tributario.', ruta: '/financiero/emision-config', tituloTab: 'Configuración de Emisión' },
];

const FAQ: { q: string; r: string }[] = [
  { q: '¿Factura o boleta? ¿Quién decide?', r: 'El sistema lo decide por el documento del cliente: RUC de 11 dígitos → FACTURA (serie F, envío inmediato). DNI, CE o pasaporte → BOLETA (serie B). No hay que elegirlo a mano.' },
  { q: '¿Qué es el CDR y por qué importa?', r: 'La Constancia De Recepción es la respuesta firmada por SUNAT a cada envío: código 0 = aceptado (guárdala, es tu prueba legal), 2000–3999 = rechazado, 4000+ = aceptado con observaciones. El sistema la guarda automáticamente junto al comprobante.' },
  { q: '¿Por qué mi boleta no aparece "autorizada" al instante?', r: 'Las boletas NO se envían una por una: SUNAT las recibe agrupadas en el Resumen Diario (RC), que el sistema arma y envía solo. Quedan "pendiente de resumen" unas horas y pasan a autorizadas cuando SUNAT procesa el lote. Es el flujo normal B2C.' },
  { q: '¿Qué diferencia hay entre "rechazado" y "error"?', r: 'RECHAZADO = SUNAT recibió y devolvió un código 2000–3999: ese número de comprobante quedó quemado y hay que corregir y emitir con número nuevo. ERROR = ni siquiera se logró informar (credenciales, red, estructura): se corrige la causa y se REINTENTA con el mismo documento.' },
  { q: '¿Cómo anulo un comprobante ya aceptado?', r: 'Facturas y NC aceptadas se anulan con la Comunicación de Baja (RA) dentro de los 7 días: pantalla Comunicación de Baja, seleccionas los documentos y el motivo. Las boletas se corrigen por el propio Resumen Diario. Un comprobante rechazado no se anula: nunca existió para SUNAT.' },
  { q: '¿Qué es el código QR del PDF?', r: 'Es obligatorio en la representación impresa (RIDE): contiene RUC, tipo y número, IGV, total, fecha, documento del cliente y el hash de la firma. Permite a cualquiera validar el comprobante.' },
  { q: '¿Puedo facturar en dólares?', r: 'Sí: el comprobante puede ir en PEN o USD. El sistema registra además el tipo de cambio del día (SBS) para el reporte consolidado del grupo en USD.' },
  { q: '¿Qué pasa si SUNAT está caído o no hay internet?', r: 'El comprobante queda "encolado" o en "error" y el motor lo reintenta. No se pierde: cuando el servicio vuelve, se envía con el mismo número (solo los RECHAZADOS queman numeración).' },
  { q: '¿Los correlativos pueden tener huecos?', r: 'SUNAT tolera saltos de correlativo (a diferencia del SRI ecuatoriano no exige secuencia perfecta), pero el sistema los evita reutilizando lotes no enviados. Un rechazo definitivo sí deja el número consumido.' },
];

const GLOSARIO: { t: string; d: string }[] = [
  { t: 'SUNAT', d: 'Superintendencia Nacional de Aduanas y de Administración Tributaria — el "SRI" del Perú.' },
  { t: 'Clave SOL', d: 'Credenciales de SUNAT Operaciones en Línea. Para emitir se usa un usuario SECUNDARIO con perfil "Envío de comprobantes"; en beta el genérico MODDATOS.' },
  { t: 'SEE — Sistemas del Contribuyente', d: 'Modalidad de emisión electrónica donde TU software genera, firma y envía los comprobantes directamente a SUNAT (la que usa este ERP; sin OSE/PSE de por medio).' },
  { t: 'UBL 2.1', d: 'El formato XML estándar internacional en el que SUNAT exige los comprobantes (distinto del XML del SRI ecuatoriano).' },
  { t: 'Firma XMLDSig / .p12', d: 'Firma digital del XML con el certificado de la empresa. El .p12 contiene la clave privada; en producción lo emite una entidad certificadora autorizada.' },
  { t: 'CDR', d: 'Constancia De Recepción: la respuesta firmada de SUNAT (aceptado / rechazado / observado). Es el sustento legal del comprobante.' },
  { t: 'Factura (01)', d: 'Comprobante B2B para clientes con RUC. Da derecho a crédito fiscal. Se envía y acepta al instante (síncrono).' },
  { t: 'Boleta de venta (03)', d: 'Comprobante B2C para consumidores finales (DNI/CE/pasaporte). No da crédito fiscal. Se informa por Resumen Diario.' },
  { t: 'Nota de crédito (07)', d: 'Anula o corrige una factura/boleta: siempre referencia al documento afectado y lleva un motivo del catálogo SUNAT.' },
  { t: 'Resumen Diario (RC)', d: 'Lote automático que informa a SUNAT las boletas del día. Asíncrono: SUNAT devuelve un ticket y luego el resultado.' },
  { t: 'Comunicación de Baja (RA)', d: 'Anulación formal de comprobantes YA aceptados (hasta 7 días). También asíncrona, por ticket.' },
  { t: 'Serie y correlativo', d: 'Numeración del comprobante: serie de 4 caracteres (F001, B001) + número. El nombre del archivo XML es RUC-TIPO-SERIE-NÚMERO.' },
  { t: 'RIDE / representación impresa', d: 'El PDF del comprobante para el cliente, con el QR obligatorio. El documento tributario real es el XML firmado + su CDR.' },
  { t: 'IGV', d: 'Impuesto General a las Ventas (18%). Cada línea lleva su afectación: gravada, exonerada, inafecta o exportación.' },
  { t: 'Ambiente beta / producción', d: 'SUNAT ofrece un ambiente de PRUEBAS (e-beta, credenciales MODDATOS, sin valor tributario) idéntico al real. Todo se valida ahí antes de pasar a producción.' },
];

function Acordeon({ titulo, children }: { titulo: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border rounded-md">
      <button className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-left hover:bg-muted/50" onClick={() => setOpen((o) => !o)}>
        {open ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
        {titulo}
      </button>
      {open && <div className="px-4 pb-3 text-sm text-muted-foreground whitespace-pre-line">{children}</div>}
    </div>
  );
}

/** `embebida`: se muestra como pestaña dentro de la Ayuda del módulo, que ya
 *  puso el título y el padding. Suelta (por su ruta propia) conserva los suyos. */
export function AyudaSunatPe({ embebida = false }: { embebida?: boolean } = {}) {
  const { data, isFetching } = useQuery<Estado>({
    queryKey: ['fe-pe-ayuda'],
    queryFn: async () => (await apiClient.post<Estado>('/rpc/fe_pe_ayuda_estado', {}, FIN)).data,
    staleTime: 0,
  });

  if (isFetching && !data) return <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>;

  if (data && !data.aplica) {
    return (
      <div className="p-6 max-w-3xl">
        <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">
          Esta guía es para empresas de <b>Perú</b> (facturación SUNAT). La empresa actual está configurada con país <b>{data.pais ?? '—'}</b>.
        </CardContent></Card>
      </div>
    );
  }

  const ck = data?.checklist;
  const st = data?.stats;
  const esBeta = ck?.ambiente !== '2';
  const flag = (k: keyof Estado['checklist']) => !!(ck && ck[k]);

  return (
    <div className={embebida ? 'space-y-5' : 'p-6 space-y-5 max-w-5xl'}>
      <div className="flex items-center gap-3 flex-wrap">
        {!embebida && (
          <h1 className="text-xl font-semibold flex items-center gap-2"><LifeBuoy className="w-5 h-5" /> Facturación electrónica Perú (SUNAT)</h1>
        )}
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${esBeta ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'}`}>
          ambiente: {esBeta ? 'PRUEBAS (beta — sin valor tributario)' : 'PRODUCCIÓN'}
        </span>
        <span className="text-xs text-muted-foreground ml-auto">{data?.empresa} · RUC {data?.ruc ?? '—'}</span>
      </div>
      <p className="text-sm text-muted-foreground -mt-2">
        El sistema genera el XML (UBL 2.1), lo firma con tu certificado digital, lo envía a SUNAT y guarda la constancia (CDR) y el
        PDF con QR — todo automático al facturar. Esta guía te dice qué configurar, cómo funciona y qué significa cada término.
      </p>

      {/* 1 · Checklist en vivo */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><ListChecks className="w-4 h-4" /> Listo para operar — estado real de la configuración</CardTitle></CardHeader>
        <CardContent className="space-y-1.5">
          {CHECKS.map((c) => (
            <div key={c.key} className="flex items-start gap-2 text-sm py-1 border-b last:border-0 border-dashed">
              {flag(c.key)
                ? <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                : c.advisory
                  ? <HelpCircle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                  : <XCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />}
              <div className="min-w-0">
                <span className="font-medium">{c.label}</span>
                {c.key === 'series' && ck && <span className="text-xs text-muted-foreground"> — {ck.serie_factura ?? '—'} / {ck.serie_boleta ?? '—'} / {ck.serie_nc ?? '—'}</span>}
                <p className="text-xs text-muted-foreground">{c.ayuda}</p>
              </div>
              {!flag(c.key) && c.ruta && <span className="ml-auto shrink-0"><Ir to={c.ruta} title={c.titulo ?? ''}>Configurar</Ir></span>}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* 2 · Estado de la emisión */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><BadgeCheck className="w-4 h-4" /> Estado de la emisión</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-center">
            {[['Autorizados', st?.autorizados, 'text-green-600'], ['En cola', st?.encolados, 'text-amber-600'],
              ['Boletas x resumen', st?.boletas_pendientes, 'text-amber-600'], ['Rechazados', st?.rechazados, 'text-red-600'],
              ['Con error', st?.errores, 'text-red-600']].map(([l, v, cls]) => (
              <div key={l as string} className="border rounded-md py-2">
                <div className={`text-xl font-bold ${cls}`}>{(v as number) ?? 0}</div>
                <div className="text-[11px] text-muted-foreground">{l}</div>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Último autorizado: {st?.ultimo_autorizado ? new Date(st.ultimo_autorizado).toLocaleString('es-PE', { hour12: false }) : '—'} ·
            Resúmenes/bajas enviados: {st?.resumenes ?? 0} · Los documentos y su estado SUNAT se ven en las pantallas de facturación;
            los reintentos, en el <Ir to="/sistemas/procesos" title="Monitor de procesos">Monitor de procesos</Ir>
          </p>
        </CardContent>
      </Card>

      {/* 3 · Cómo funciona */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><BookOpen className="w-4 h-4" /> Cómo funciona (3 flujos)</CardTitle></CardHeader>
        <CardContent className="grid sm:grid-cols-3 gap-3 text-sm">
          <div className="border rounded-md p-3">
            <p className="font-medium">Factura / NC — al instante</p>
            <p className="text-xs text-muted-foreground mt-1">Cliente con RUC. El sistema arma el XML, lo firma y lo envía: SUNAT responde en segundos con el CDR (aceptado/rechazado). El PDF con QR queda listo para el cliente.</p>
          </div>
          <div className="border rounded-md p-3">
            <p className="font-medium">Boletas — por Resumen Diario</p>
            <p className="text-xs text-muted-foreground mt-1">Cliente con DNI. La boleta se emite y entrega YA (PDF con QR); SUNAT la recibe agrupada en el Resumen Diario que el sistema envía solo y consulta hasta la aceptación.</p>
          </div>
          <div className="border rounded-md p-3">
            <p className="font-medium flex items-center gap-1"><FileX2 className="w-3.5 h-3.5" /> Anulaciones — Comunicación de Baja</p>
            <p className="text-xs text-muted-foreground mt-1">Facturas/NC ya aceptadas se dan de baja (hasta 7 días) con motivo. <Ir to="/financiero/comunicacion-baja" title="Comunicación de Baja">Abrir pantalla</Ir></p>
          </div>
        </CardContent>
      </Card>

      {/* 4 · Pasos de implementación */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Rocket className="w-4 h-4" /> Pasos de implementación (de cero a producción)</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {PASOS.map((p) => (
            <div key={p.n} className="flex gap-3 text-sm">
              <div className="w-6 h-6 rounded-full bg-primary/10 text-primary font-bold text-xs flex items-center justify-center shrink-0">{p.n}</div>
              <div>
                <span className="font-medium">{p.titulo}</span>
                {p.ruta && <span className="ml-2"><Ir to={p.ruta} title={p.tituloTab ?? ''} /></span>}
                <p className="text-xs text-muted-foreground">{p.detalle}</p>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* 5 · FAQ */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><HelpCircle className="w-4 h-4" /> Preguntas frecuentes</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {FAQ.map((f) => <Acordeon key={f.q} titulo={f.q}>{f.r}</Acordeon>)}
        </CardContent>
      </Card>

      {/* 6 · Glosario */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><BookOpen className="w-4 h-4" /> Glosario SUNAT</CardTitle></CardHeader>
        <CardContent className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
          {GLOSARIO.map((g) => (
            <div key={g.t} className="text-sm border-b border-dashed pb-1.5">
              <span className="font-medium">{g.t}</span>
              <p className="text-xs text-muted-foreground">{g.d}</p>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
